import React, { useEffect, useRef, useState } from "react";

import { useFormikContext } from "formik";
import * as yup from "yup";

import {
    FormikAutocompleteInput,
    FormikTextInput,
} from "components/UI/Inputs/FormikInputs";

import {
    CARACTERES_PERMITIDOS_MSG,
    CARACTERES_PERMITIDOS_REGEX,
    sanitizarTexto,
} from "consts/validaciones";
import { localidadesGet } from "services/services-mia/localidadesGet";

import MapaPreview from "./MapaPreview";
import { CENTRO_POR_DEFECTO } from "./googleMaps";
import { LEYENDAS, LOCALIZACION, round6 } from "./useUbicacion";

import styles from "./BloqueUbicacion.module.scss";

/**
 * Las clases del bloque, para los campos que cada formulario le pasa por `camposAntes`.
 *
 * Se exportan a proposito. Los modulos de CSS generan un nombre distinto por archivo, asi que
 * las columnas de la grilla (col4, col6, col8, col12) solo existen con el nombre que les puso
 * ESTE modulo: un formulario que usara las clases de su propio archivo no le acertaria a
 * ninguna y sus campos entrarian sin ancho, uno por fila.
 */
export const estilos = styles;

/**
 * Bloque de domicilio y geolocalizacion, compartido por los establecimientos.
 *
 * Hospitales, CAPS, puestos, postas y URESA son la misma entidad del back (RNHEstablecimiento
 * con distinto rnhTipoComponenteCod) y resuelven su direccion igual: mismos campos, misma
 * lista de localidades y mismo mapa con pin arrastrable. Vive aca una sola vez para que un
 * arreglo no haya que aplicarlo en diez formularios.
 *
 * A diferencia del panel de CH, que es el mismo bloque para varias provincias, aca no hay
 * select de provincia: el panel administra solo Rio Negro, las localidades se piden siempre
 * con PROVINCIACOD y la entidad guarda localidadId sin provincia. La provincia entra igual en
 * la consulta de geocoding, como constante (ver PROVINCIA_NOMBRE en utils/constants).
 */

export const LARGOS = {
    calle: 45,
    numero: 10,
    // 8 es exactamente el formato del CPA argentino: una letra de provincia, cuatro digitos
    // del codigo postal y tres letras de manzana ("R8500ABC". No hay nada valido mas largo.
    cpa: 8,
};

/**
 * Lo que se guarda cuando la calle no tiene altura.
 *
 * El back sigue pidiendo el numero, asi que dejarlo vacio no es una opcion: se manda esto.
 *
 * La barra NO se puede tipear —no esta en CARACTERES_PERMITIDOS y el sanitize del onChange la
 * borra—, y eso es a proposito: el usuario no escribe "S/N", deja el campo vacio y el
 * formulario lo planta al enviar. Por lo mismo, al abrir una modificacion de un registro que
 * ya tiene "S/N" el campo arranca VACIO: si entrara con la barra puesta, el schema lo
 * rechazaria al guardar y el usuario no tendria como arreglarlo.
 */
export const SIN_NUMERO = "S/N";

/** Si un numero guardado es en realidad la marca de "sin altura". */
export const esSinNumero = (valor) =>
    String(valor ?? "").trim().toUpperCase() === SIN_NUMERO;

const CLASES_COL = {
    4: styles.col4,
    6: styles.col6,
    8: styles.col8,
    12: styles.col12,
};

/**
 * Traduce un mapa de campo -> ancho en columnas a las clases de la grilla.
 *
 * Se exporta para la vista de solo lectura, que dibuja los mismos campos con los mismos anchos
 * y no tendria de donde sacar las clases: los modulos de CSS son por archivo.
 */
export const clasesDeColumnas = (anchos) =>
    Object.fromEntries(
        Object.entries(anchos).map(([campo, ancho]) => [campo, CLASES_COL[ancho]])
    );

/**
 * Anchos por defecto del domicilio, en columnas de la grilla de 12.
 *
 * Son cuatro filas y no tres a proposito. La columna del mapa tiene un alto minimo —el mapa,
 * la leyenda y los dos campos de coordenadas— y con el domicilio en tres filas la columna de
 * carga terminaba antes, asi que latitud y longitud quedaban colgando por debajo del ultimo
 * campo del formulario. Con cuatro filas las dos columnas miden casi lo mismo y el mapa, que
 * es flexible, absorbe lo que sobre.
 *
 * Localidad se lleva la fila entera porque es donde caen los nombres largos ("San Antonio
 * Oeste", "Comallo"), y el CPA cierra solo: es de cinco caracteres y no gana nada con mas
 * ancho.
 */
// Calle 8 + Numero 4 arriba, Localidad 8 + CPA 4 abajo: las dos filas cierran en 12
// y el CPA queda alineado debajo del Numero.
export const ANCHOS_DOMICILIO = { calle: 8, numero: 4, localidad: 8, cpa: 4 };

/**
 * Los mismos campos, de a uno por fila, para la columna angosta de `mapaAncho`.
 *
 * Con el mapa llevandose 6 de 10 partes, la columna de carga queda en unos 380px: dos campos
 * por fila dejarian al de 4 columnas en 120px, que para "Número" o "CPA" alcanza justo pero se
 * lee apretado y el label no entra entero.
 *
 * Que sume filas no es un efecto colateral, es parte del arreglo: el mapa se estira hasta
 * igualar a la columna de carga, asi que cada fila que se suma de este lado es alto que gana
 * el mapa del otro.
 */
export const ANCHOS_DOMICILIO_ANGOSTO = {
    calle: 12,
    numero: 12,
    localidad: 12,
    cpa: 12,
};

/**
 * El angosto, pero con Calle y Numero COMPARTIENDO renglon, en el mismo 8/4 que Nombre y
 * Codigo arriba.
 *
 * Lo usan los establecimientos que ademas llevan los permisos de reparticion: el renglon
 * que se ahorra acá es el que ocupan los dos checkbox, asi que el formulario mide lo mismo
 * que antes y el mapa no se achica.
 *
 * Sacrifica lo que dice la nota del angosto —que a 120px "Número" se lee apretado—, pero a
 * cambio las dos filas de arriba cortan en el mismo lugar, que es lo que hace que se lean
 * alineadas. El Codigo ya venia midiendo eso mismo sin que molestara.
 */
export const ANCHOS_DOMICILIO_ANGOSTO_COMPACTO = {
    calle: 8,
    numero: 4,
    localidad: 12,
    cpa: 12,
};

const texto = (largo) =>
    yup
        .string()
        .max(largo, `Debe tener menos de ${largo} caracteres`)
        .matches(CARACTERES_PERMITIDOS_REGEX, CARACTERES_PERMITIDOS_MSG);

/**
 * Reglas de los campos del bloque, para que el formulario las sume a su propio schema.
 *
 * Calle, CPA y localidad son obligatorios porque el back los pide: mandarlos vacios no da un
 * formulario a medio llenar, da un error del servicio despues de haber cargado todo. El CPA
 * venia opcional en cuatro de las cinco entidades y requerido solo en CAPS, asi que en las
 * otras el alta se intentaba y volvia rechazada.
 *
 * El numero es la excepcion: el back tambien lo pide, pero hay calles sin altura, asi que el
 * formulario lo deja vacio y manda `SIN_NUMERO` en su lugar.
 */
export const reglasUbicacion = () => ({
    calle: texto(LARGOS.calle).required("Debe ingresar una calle"),
    // Sin `required`: hay calles sin altura. El vacio no llega al back, se convierte en
    // `SIN_NUMERO` al enviar.
    numero: texto(LARGOS.numero),
    cpa: texto(LARGOS.cpa).required("Debe ingresar un CPA"),
    localidadId: yup.string().required("Debe seleccionar una localidad"),
});

/**
 * Los campos del bloque, vacios, para los initialValues de un alta.
 *
 * lat y lng arrancan en el centro por defecto (Viedma): el mapa tiene que mostrar algo, y el
 * guard de useUbicacion es el que impide guardar sin haber elegido la ubicacion de verdad.
 */
export const valoresUbicacionVacios = () => ({
    calle: "",
    numero: "",
    cpa: "",
    localidadId: "",
    lat: round6(CENTRO_POR_DEFECTO.lat),
    lng: round6(CENTRO_POR_DEFECTO.lng),
});

/**
 * Busca la direccion en el mapa cuando el domicilio esta cargado y cambia.
 *
 * Dispara con la localidad elegida —es el ultimo campo, asi que es la señal de que el domicilio
 * esta completo— y tambien cuando despues se corrige la calle o el numero, que era el agujero
 * que quedaba: se elegia la localidad, se arreglaba la altura y el pin se quedaba en el lugar
 * viejo.
 *
 * Lo que evita geocodificar por tecla son dos cosas:
 *
 *   - La clave del domicilio. Se guarda con que direccion se busco la ultima vez y no se
 *     vuelve a buscar la misma. Eso cubre el primer render (en la modificacion el domicilio
 *     viene del registro, con su coordenada, y buscar de nuevo pisaria el pin guardado) y la
 *     llegada asincronica de la lista de localidades, que re-corre el efecto sin que el
 *     usuario haya tocado nada.
 *
 *   - La espera. Escribir "3360" son cuatro cambios; con el timer, cada tecla cancela el
 *     anterior y se consulta una sola vez, cuando el usuario para. Google factura por
 *     consulta, asi que esto es plata.
 *
 * Elegir en un select, en cambio, es un solo evento y no hay nada que esperar: ahi va sin
 * demora, para que el mapa responda en el acto.
 *
 * Va como componente aparte y no como efecto del bloque porque necesita los values de Formik,
 * y aislado hace que una tecla re-renderice esto y no el formulario entero, que tiene el mapa
 * y los selects adentro.
 */
const MS_ESPERA_TIPEO = 900;

/**
 * Campo de texto que limpia lo que se tipea con la lista de caracteres permitidos.
 *
 * Se exporta porque los campos propios de cada entidad (codigo, nombre) necesitan lo mismo y
 * asi no repiten el onChange.
 *
 * El onChange va DESPUES del spread de props a proposito: FormikTextInput esparce {...field} y
 * despues {...props}, asi que el handler que se le pasa reemplaza al de Formik y el valor hay
 * que setearlo a mano. Con useFormikContext y no con el formikRef del formulario, que no
 * siempre esta poblado en el primer render.
 */
export function CampoTexto({ name, ...props }) {
    const { setFieldValue } = useFormikContext();

    return (
        <FormikTextInput
            name={name}
            {...props}
            onChange={(evento) =>
                setFieldValue(name, sanitizarTexto(evento.target.value))
            }
        />
    );
}

const nombreDeLocalidad = (localidades, id) =>
    (localidades ?? []).find(
        (item) => String(item.localidadId) === String(id)
    )?.localidadNombre;

function BusquedaDelDomicilio({ localidades, onBuscar }) {
    const { values } = useFormikContext();

    const calle = (values?.calle || "").trim();
    const numero = String(values?.numero || "").trim();
    const localidadId = values?.localidadId ?? "";

    const clave = `${calle}|${numero}|${localidadId}`;
    // Arranca con el domicilio con el que se abrio el formulario: asi el primer render no busca.
    const claveBuscadaRef = useRef(clave);

    useEffect(() => {
        if (claveBuscadaRef.current === clave) return undefined;

        // Sin localidad no hay a donde caer si la calle no se encuentra, asi que no se busca.
        // La clave avanza igual: no hay nada pendiente para este domicilio.
        if (!localidadId) {
            claveBuscadaRef.current = clave;
            return undefined;
        }

        const localidad = nombreDeLocalidad(localidades, localidadId);
        // La lista todavia no llego. Se reintenta cuando llegue, porque la clave no avanza
        // hasta haber podido buscar.
        if (!localidad) return undefined;

        // Si lo que cambio fue la localidad, no se espera: fue un click en un select.
        const cambioLaLocalidad =
            claveBuscadaRef.current.split("|")[2] !== String(localidadId);

        const timer = setTimeout(
            () => {
                claveBuscadaRef.current = clave;
                onBuscar({ calle, numero, localidad });
            },
            cambioLaLocalidad ? 0 : MS_ESPERA_TIPEO
        );

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clave, localidades]);

    return null;
}

/**
 * El color del badge de la leyenda, por estado del circuito.
 *
 * Cada estado dice algo distinto y no puede verse igual: la instruccion inicial es informativa,
 * "encontrada" es un resultado bueno, "aproximada" pide mover el pin y "falta ubicar" es lo
 * unico que impide guardar. Antes los cinco compartian un solo estilo con dos tonos.
 */
const CLASE_LEYENDA = {
    [LOCALIZACION.PENDIENTE]: "leyendaInfo",
    [LOCALIZACION.BUSCANDO]: "leyendaNeutra",
    [LOCALIZACION.ENCONTRADA]: "leyendaOk",
    [LOCALIZACION.APROXIMADA]: "leyendaAviso",
    [LOCALIZACION.FALTA_UBICAR]: "leyendaError",
};

/**
 * Ademas del domicilio y la geolocalizacion, este componente es dueño del layout del cuerpo
 * del formulario: el domicilio no puede ser hermano del mapa. Como las cinco entidades
 * comparten la disposicion, vive aca y cada formulario le pasa sus propios campos por
 * `camposAntes`.
 *
 * Siempre son dos columnas —domicilio a la izquierda, mapa a la derecha— y lo unico que cambia
 * entre entidades es como se reparten: mitades, o 40/60 a favor del mapa. Sale del mismo
 * markup, cambiando la grilla del contenedor y los anchos de los campos.
 *
 * Los campos del formulario van TODOS en la misma grilla: los propios de la entidad primero y
 * el domicilio despues, para que todas las filas tengan el mismo ritmo vertical.
 *
 * @param ubicacion   lo que devuelve useUbicacion.
 * @param camposAntes campos propios de la entidad, inyectados al PRINCIPIO del grid. Es donde
 *                    cada formulario pone su identificacion (codigo, nombre) y lo que sea
 *                    suyo: nivel, zona, area y cabecera en el caso de hospitales.
 * @param camposDespues lo mismo pero al FINAL, debajo del domicilio. Es para lo que no es
 *                    identificacion ni domicilio y conviene leer ultimo, como los permisos
 *                    de la reparticion.
 * @param columnas    anchos en columnas de la grilla de 12, por campo del domicilio. El layout
 *                    puede ser de cada entidad; lo que comparten las cinco es el
 *                    comportamiento (mapa, geocoding, guard).
 * @param mapaAncho   true (por defecto) reparte 40/60 a favor del mapa y pone los campos del
 *                    domicilio de a uno por fila. Con false quedan mitades, que es lo que
 *                    necesita hospitales por la cantidad de campos que tiene.
 */
export default function BloqueUbicacion({
    credentials,
    ubicacion,
    camposAntes = null,
    camposDespues = null,
    columnas = null,
    mapaAncho = true,
}) {
    const [localidades, setLocalidades] = useState([]);

    useEffect(() => {
        let vigente = true;
        (async () => {
            // localidadesGet ya filtra por PROVINCIACOD y devuelve la lista, no el envelope.
            const result = await localidadesGet(credentials);
            if (vigente) setLocalidades(result ?? []);
        })();

        return () => {
            vigente = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const col = clasesDeColumnas(
        columnas ?? (mapaAncho ? ANCHOS_DOMICILIO_ANGOSTO : ANCHOS_DOMICILIO)
    );


    return (
        <div
            className={`${styles.cuerpo} ${mapaAncho ? styles.cuerpoMapaAncho : ""}`}
        >
            <BusquedaDelDomicilio
                localidades={localidades}
                onBuscar={ubicacion.ubicar}
            />

            <div className={styles.columna}>
                <section className={styles.section}>
                    <div className={styles.grid}>
                        {/* Campos propios de la entidad, al principio del grid: comparten la
                            grilla con el domicilio para que todas las filas del formulario
                            tengan el mismo ritmo. */}
                        {camposAntes}
                        <CampoTexto
                            className={col.calle}
                            name="calle"
                            labelText="Calle"
                            placeholder="Calle"
                            inputProps={{ maxLength: LARGOS.calle }}
                        />
                        <CampoTexto
                            className={col.numero}
                            name="numero"
                            labelText="Número"
                            // El placeholder es el aviso de que el vacio no queda vacio:
                            // lo que se guarda si no se completa es "S/N".
                            placeholder={SIN_NUMERO}
                            inputProps={{ maxLength: LARGOS.numero }}
                        />
                        {/* Con buscador y no un desplegable: la lista de Rio Negro es
                            larga y encontrar una localidad recorriendola a mano no
                            escala. Formik sigue guardando `localidadId`, asi que el
                            resto del bloque (la busqueda del domicilio, el payload) no
                            se entera del cambio. */}
                        <FormikAutocompleteInput
                            className={col.localidad}
                            name="localidadId"
                            labelText="Localidad"
                            options={localidades}
                            optionValue="localidadId"
                            optionLabel="localidadNombre"
                            // atado a localidades y no a formikRef: el render de este
                            // componente no se vuelve a ejecutar cuando cambia un valor de
                            // Formik, pero si cuando cambia este state
                            disabled={!localidades.length}
                            placeholder={
                                localidades.length ? "Buscar localidad..." : "Cargando..."
                            }
                        />
                        <CampoTexto
                            className={col.cpa}
                            name="cpa"
                            labelText="Código postal (CPA)"
                            placeholder="R8500"
                            inputProps={{ maxLength: LARGOS.cpa }}
                        />
                        {camposDespues}
                    </div>
                </section>
            </div>

            <section
                className={`${styles.section} ${styles.conTitulo} ${styles.geoSection}`}
            >
                <h3 className={styles.sectionTitle}>Geolocalización</h3>
                <div className={styles.mapPanel}>
                    <div className={styles.mapCard}>
                        <div className={styles.mapContainer}>
                            {/* Arrastrar el pin no reescribe ningun campo de la direccion ni
                                geocodifica al reves. En el otro sentido si: cuando el domicilio
                                esta completo y cambia, se busca la direccion y el pin se mueve. */}
                            <MapaPreview
                                lat={ubicacion.coordenadas.lat}
                                lng={ubicacion.coordenadas.lng}
                                centro={ubicacion.centro}
                                onArrastrarPin={ubicacion.moverPin}
                            />
                        </div>
                    </div>
                    <p
                        className={`${styles.leyenda} ${styles[CLASE_LEYENDA[ubicacion.estado]] ?? ""}`}
                    >
                        {LEYENDAS[ubicacion.estado]}
                    </p>
                    <div className={`${styles.grid} ${styles.coordenadas}`}>
                        <FormikTextInput
                            className={styles.col6}
                            name="lat"
                            labelText="Latitud"
                            disabled={true}
                        />
                        <FormikTextInput
                            className={styles.col6}
                            name="lng"
                            labelText="Longitud"
                            disabled={true}
                        />
                    </div>
                </div>
            </section>
        </div>
    );
}
